import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatJPY, formatDate } from "@/lib/format";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart as RPieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Users,
  FileText,
  Loader2,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const PIE_COLORS = ["#064e3b", "#0f766e", "#15803d", "#65a30d", "#ca8a04", "#ea580c", "#92400e", "#57534e"];

const KpiCard = ({ icon: Icon, label, value, hint, accent = "emerald", testid }) => {
  const accentMap = {
    emerald: "bg-emerald-50 text-emerald-900",
    orange: "bg-orange-50 text-orange-700",
    stone: "bg-stone-100 text-stone-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accentMap[accent]}`}>
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </div>
      </div>
      <div className="mt-4 text-xs uppercase tracking-[0.1em] font-semibold text-stone-500">{label}</div>
      <div className="mt-1 font-numbers font-bold text-2xl text-stone-900 truncate">{value}</div>
      {hint && <div className="mt-1 text-xs text-stone-500">{hint}</div>}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.is_platform_admin || user?.role === "admin") return;
    let mounted = true;
    api
      .get("/reports/dashboard")
      .then((res) => mounted && setData(res.data))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [user]);

  // プラットフォーム管理者は運営管理画面へ
  if (user?.is_platform_admin || user?.role === "admin") {
    return <Navigate to="/app/admin" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-900" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-stone-500">データを取得できませんでした。</div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">ダッシュボード</div>
          <h1 className="mt-1 font-display font-bold text-3xl tracking-tight text-stone-900">
            今日の経営状況
          </h1>
          <p className="mt-1 text-sm text-stone-500">{formatDate(new Date().toISOString())} 時点</p>
        </div>
        <Link
          to="/app/billing/invoices"
          data-testid="dashboard-create-invoice"
          className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-950"
        >
          <FileText className="h-4 w-4" /> 請求書を作成
        </Link>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-revenue-paid"
          icon={TrendingUp}
          label="売上 (入金済)"
          value={formatJPY(data.revenue_paid)}
          hint={`未収: ${formatJPY(data.revenue_outstanding)}`}
          accent="emerald"
        />
        <KpiCard
          testid="kpi-expenses"
          icon={TrendingDown}
          label="経費 合計"
          value={formatJPY(data.total_expenses)}
          accent="orange"
        />
        <KpiCard
          testid="kpi-net-profit"
          icon={Wallet}
          label="純利益"
          value={formatJPY(data.net_profit)}
          hint={data.net_profit >= 0 ? "黒字" : "赤字"}
          accent={data.net_profit >= 0 ? "emerald" : "orange"}
        />
        <KpiCard
          testid="kpi-customers"
          icon={Users}
          label="取引先 / 請求書"
          value={`${data.customers_count} / ${data.invoices_count}`}
          accent="stone"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-stone-200 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-semibold text-lg text-stone-900">月次 売上 vs 経費</h2>
              <p className="text-xs text-stone-500 mt-0.5">過去6ヶ月</p>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly_series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" vertical={false} />
                <XAxis dataKey="month" stroke="#a8a29e" fontSize={11} />
                <YAxis stroke="#a8a29e" fontSize={11} tickFormatter={(v) => `¥${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(v) => formatJPY(v)}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e7e5e4", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" name="売上" fill="#064e3b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="経費" fill="#ea580c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-stone-200 bg-white p-6">
          <h2 className="font-display font-semibold text-lg text-stone-900">経費カテゴリ</h2>
          <p className="text-xs text-stone-500 mt-0.5 mb-4">合計に対する内訳</p>
          {data.expense_categories.length === 0 ? (
            <p className="text-sm text-stone-500 py-8 text-center">経費データがありません</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RPieChart>
                  <Pie
                    data={data.expense_categories}
                    dataKey="amount"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                  >
                    {data.expense_categories.map((entry, idx) => (
                      <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatJPY(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e7e5e4", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </RPieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Recent invoices */}
      <div className="rounded-xl border border-stone-200 bg-white">
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div>
            <h2 className="font-display font-semibold text-lg text-stone-900">最近の請求書</h2>
            <p className="text-xs text-stone-500 mt-0.5">直近5件</p>
          </div>
          <Link
            to="/app/billing/invoices"
            data-testid="dashboard-view-all-invoices"
            className="inline-flex items-center gap-1 text-sm text-emerald-800 hover:text-emerald-950"
          >
            すべて見る <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {data.recent_invoices.length === 0 ? (
          <div className="p-10 text-center text-sm text-stone-500">
            まだ請求書がありません。<Link to="/app/billing/invoices" className="text-emerald-800 link-underline">最初の請求書を作成</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-stone-500 uppercase tracking-wider bg-stone-50/50 border-b border-stone-200">
                  <th className="px-6 py-3 text-left font-semibold">請求書 No.</th>
                  <th className="px-6 py-3 text-left font-semibold">取引先</th>
                  <th className="px-6 py-3 text-left font-semibold">発行日</th>
                  <th className="px-6 py-3 text-right font-semibold">金額</th>
                  <th className="px-6 py-3 text-left font-semibold">ステータス</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`dashboard-invoice-row-${inv.id}`}>
                    <td className="px-6 py-3 font-medium text-stone-900">{inv.invoice_number}</td>
                    <td className="px-6 py-3 text-stone-700">{inv.customer_name}</td>
                    <td className="px-6 py-3 text-stone-500">{formatDate(inv.issue_date)}</td>
                    <td className="px-6 py-3 text-right font-numbers font-medium text-stone-900">{formatJPY(inv.total)}</td>
                    <td className="px-6 py-3">
                      <StatusBadge status={inv.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: { label: "下書き", cls: "bg-stone-100 text-stone-700 border-stone-200" },
    sent: { label: "送付済", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    paid: { label: "入金済", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    overdue: { label: "期限超過", cls: "bg-orange-50 text-orange-800 border-orange-200" },
  };
  const info = map[status] || map.draft;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${info.cls}`}>
      {info.label}
    </span>
  );
}
